import React, { FC } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import DatePickerDialog from 'components/shared/date-picker-dialog/date-picker-dialog.component';
import { updateReadyForInvoiceDateTemplate } from 'apollo/templates/order';
import { Order } from 'models/order';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

type ChangeReadyToInvoiceDateDialogProps = {
    open: boolean;
    order: Order;
    onClose: () => void;
    onUpdate?: (order: Order) => void;
};

const ChangeReadyToInvoiceDateDialog: FC<ChangeReadyToInvoiceDateDialogProps> = ({
    open,
    order,
    onClose,
    onUpdate,
}: ChangeReadyToInvoiceDateDialogProps) => {
    const { t: translate } = useTranslation(['invoices']);
    const { showBoundary } = useDenthubErrorBoundary();

    const [updateReadyForInvoiceDate, { loading }] = useMutation(updateReadyForInvoiceDateTemplate, {
        onCompleted: ({ updateReadyForInvoiceDate }) => {
            onClose();
            onUpdate?.({ ...order, invoiceActionTime: updateReadyForInvoiceDate.invoiceActionTime });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleConfirm = async (date: Date | null) => {
        await updateReadyForInvoiceDate({
            variables: {
                orderId: order._id,
                date,
            },
        });
    };

    return (
        <DatePickerDialog
            open={open}
            onClose={onClose}
            onConfirm={handleConfirm}
            title={translate('invoices:EDIT_READY_FOR_INVOICE_DATE')}
            initialDate={order?.invoiceActionTime || null}
            loading={loading}
            data-test="change-ready-to-invoice-date-dialog.date-picker"
        />
    );
};

export default ChangeReadyToInvoiceDateDialog;
